<template>
  <div v-if="isOpen" class="absolute">
    <div :id="`color-picker-wrp-${colorPickerId}`" :class="`color-picker-wrp ${{ isOpen: 'opened' }}`" :style="style">
      <div id="top" class="color-picker top" @mouseenter="onDragWrp(true)" @mouseleave="onDragWrp(false)">
        <div class="title">
          <paint-bucket class="me-2" style="fill: #007fff" />
          <div>{{ currentPanel?.seq === colorPanels[1].seq ? t('custom') : t('simple') }}</div>
        </div>
        <div class="btn btn-light" @click="onToggle(false)">X</div>
      </div>
      <div class="color-picker tabs-wrp">
        <div @click="onClickChangePanel(0)" class="tab-item" :class="currentPanel?.seq === colorPanels[0].seq && 'active'">
          {{ colorPanels[0].label }}
        </div>
        <div @click="onClickChangePanel(1)" class="tab-item" :class="currentPanel?.seq === colorPanels[1].seq && 'active'">
          {{ colorPanels[1].label }}
        </div>
      </div>
      <div>
        <div v-if="currentPanel?.seq === colorPanels[0].seq" class="color-picker color-selection-wrp color-static">
          <div class="color-grid-wrp">
            <div v-for="colorItem in colorsList" :key="colorItem.seq" class="color-item">
              <div
                @click="onClickColor(colorItem)"
                :style="{ backgroundColor: colorItem.colorHex, border: colorItem.colorHex.toUpperCase() === '#FFFFFF' ? '1px solid #CCCCCC' : '' }"
              >
                <check-icon v-if="colorItem === currentColor" :style="{ stroke: colorItem.colorHex.toUpperCase() === '#FFFFFF' && '#CCCCCC' }" />
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="currentPanel?.seq === colorPanels[1].seq" class="color-picker color-selection-wrp color-selection-wrp-tran custom">
          <div class="color-range" :key="String(isDragging)">
            <color-spectrum
              :wrapperId="`color-picker-wrp-${colorPickerId}`"
              :currentColor="currentColor?.colorHex"
              @on-change-current-color="colorSpectrumHandler"
            />
          </div>
        </div>
      </div>

      <div class="color-picker bottom-wrp">
        <div class="d-flex mb-2 align-items-center">
          {{ t('color_swatches') }}
          <div v-if="localColors.length" class="d-inline ml-2">
            <div class="clear-saved-color-button text-primary" @click="onClickClearLocalColor">{{ t('clear_all') }}</div>
          </div>
        </div>

        <div class="default">
          <div v-if="localColors.length" class="saved-color-wrp">
            <div v-for="colorItem in localColors" :key="colorItem.seq" class="color-item">
              <div
                @click="onClickColor(colorItem)"
                :style="{ backgroundColor: colorItem.colorHex, border: colorItem.colorHex.toUpperCase() === '#FFFFFF' ? '1px solid #CCCCCC' : '' }"
              >
                <check-icon v-if="colorItem === currentColor" :style="{ stroke: colorItem.colorHex.toUpperCase() === '#FFFFFF' && '#CCCCCC' }" />
              </div>
            </div>
            <div class="cursor-pointer" @click="onClickAddSwatch"><plus-circle-icon /></div>
          </div>
          <div v-else class="cursor-pointer" @click="onClickAddSwatch"><plus-circle-icon /></div>
        </div>
      </div>
      <div class="color-picker bottom-wrp">
        <div class="custom">
          <button class="btn" @click="onToggle(false)">{{ t('global.cancel') }}</button>
          <button class="btn btn-primary" @click="onClickApply">{{ t('global.apply') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import useBaseColorPicker from '../color-picker/hooks/useBaseColorPicker';
import ColorSpectrum from './components/ColorSpectrum.vue';
import CheckIcon from '@/assets/icons/base/color-picker/CheckIcon.vue';
import PlusCircleIcon from '@/assets/icons/base/color-picker/PlusCircleIcon.vue';
import PaintBucket from '@/assets/icons/modules/analytics/bi/PaintBucket.vue';
import { useI18n } from 'vue-i18n';
import { watch, nextTick, ref, Ref } from 'vue';
import { useDraggable, useWindowSize } from '@vueuse/core';
const { t } = useI18n();
interface Props {
  selectedColor: string;
  isAdvance?: boolean;
  isOpen: boolean;

  // Adjust Position
  colorPickerId: string;
  parentElementSelector?: string;
  isCenter?: boolean;
  initialPosition?: XYPosition;
}

interface XYPosition {
  x: number;
  y: number;
}

const props = withDefaults(defineProps<Props>(), {
  isAdvance: false,
  isOpen: false,
  isCenter: false,
});

const elRef: Ref<HTMLElement | null> = ref(null);
const isDragging: Ref<boolean> = ref(false);

const { width, height } = useWindowSize();

// Base picker size
const elRect = { width: 226.67, height: 469.57 };

const positionInit = () => {
  if (props.isCenter || (!props.isCenter && !props.initialPosition)) {
    return {
      x: width.value / 2 - elRect.width / 2,
      y: height.value / 2 - elRect.height / 2,
    };
  } else {
    return props.initialPosition;
  }
};

const onDragWrp = async (isDrag: boolean) => {
  if (isDrag) {
    elRef.value = document.getElementById(`color-picker-wrp-${props.colorPickerId}`) as HTMLElement;
    await nextTick();
    isDragging.value = true;
  } else {
    elRef.value = null;
    await nextTick();
    isDragging.value = false;
  }
};

const { style } = useDraggable(elRef, {
  initialValue: positionInit(),
  preventDefault: true,
});

const emits = defineEmits(['on-change-current-color', 'color-picker-close', 'color-picker-open', 'apply', 'on-color-changed']);

watch(
  () => props.isOpen,
  () => {
    if (props.isOpen) {
      nextTick(() => {
        onToggle(true);
        emits('color-picker-open');
      });
    }
  },
);

const {
  colorsList,
  localColors,
  onClickColor,
  onClickChangePanel,
  onClickApply,
  onClickClearLocalColor,
  onClickAddSwatch,
  currentColor,
  currentPanel,
  colorSpectrumHandler,
  onToggle,
  colorPanels,
} = useBaseColorPicker(props);
</script>

<style lang="scss" scoped>
@import '@/assets/styles/base/base-color-picker.scss';
</style>
